// EliminarCuentaDialog.jsx · "Eliminar mi cuenta" en la Cuenta (#1683, mockup elegido 2A:
// tools/mockups/smartdx_mockups_1683_derechos.html, bloque 2 opción A: zona de peligro al final de
// la Cuenta y diálogo de dos pasos, con una variante cuando hay una suscripción activa).
//
// Flujo (R51-R55):
//  - Con una suscripción `activa` y sin cancelación programada (dato de `/api/user/me/`), el diálogo
//    abre en la variante `eliminar-cuenta-suscripcion`: primero se cancela (POST
//    /api/billing/me/subscription/cancel/). Con el 200 se refresca /me y el diálogo pasa al paso 1.
//  - Paso 1: qué se borra y qué se conserva por ley. Paso 2: escribir el correo o ELIMINAR; el botón
//    final no se habilita hasta que calce (R52, mismo criterio que el servidor: sin distinguir
//    mayúsculas ni espacios en el correo; ELIMINAR en mayúsculas).
//  - Con el 200 de POST /api/user/me/eliminar/ se hace la MISMA limpieza local que al cerrar sesión
//    (`limpiarSesionLocal`: borradores del Turno y diario del dictado) y se navega a la pantalla
//    pública `cuenta-eliminada` (R55). Navegación completa (no de React Router) para soltar todo
//    el estado en memoria de la sesión que ya no existe.
//  - La barrera de verdad vive en el servidor (403 cuenta privilegiada, 409 suscripción activa, 502
//    `flow_no_cancelo` si Flow no confirma el corte del cobro automático, 502
//    `flow_estado_desconocido` si Flow no da una respuesta válida ni se puede saber cómo quedó, 502
//    `flow_error_api` si Flow responde con un error al consultar un corte sin resolver): esto solo
//    evita ofrecer un botón que va a fallar.
//  - Cada error dice lo que pasó de verdad (revisor B4, O14/O15 y v4 B8, #1683): el servidor corta el
//    cobro de Flow ANTES de borrar y solo borra si Flow confirma, así que los tres de Flow y
//    `no_se_pudo_eliminar` son "no se borró nada" (cada uno con su texto del servidor) y llevan el
//    link de WhatsApp (`CODIGOS_CON_SOPORTE`): reintentar no siempre lo arregla y el médico necesita
//    a quién escribir; `cobro_cancelado_cuenta_no_eliminada` es "el cobro ya se canceló, la cuenta
//    sigue" (se refresca /me); y un error sin código (red, proxy) NO afirma que no se borró nada,
//    porque no lo sabemos.
//  - 410 `cuenta_ya_eliminada` (otra pestaña ya la eliminó): no hay nada que resolver ni a quién
//    escribir. El diálogo pasa a la variante `eliminada`: texto del servidor, SIN link de WhatsApp,
//    sesión local limpia en el acto (el token ya no sirve) y un único botón al ingreso; cerrar el
//    diálogo hace lo mismo, así no queda una pantalla de la cuenta sin sesión.
// El patrón de diálogo es el del admin (`DangerZoneSection`, #1544). Todo color por tokens (R61).
// Cubre R51, R52, R53, R54, R55, R60, R61 — .claude/specs/1683-derechos-usuario/
import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Dialog, Link, Stack, TextField, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { limpiarSesionLocal } from "../../helpers/authentication-helpers";
import { WHATSAPP_SOPORTE } from "../../helpers/soporte";
import {
  codigoDeError,
  postCancelarSuscripcion,
  postEliminarCuenta,
} from "../../helpers/comunicaciones-api";

const PLAN_NOMBRES = { turno: "Turno", pro: "Turno Pro", max: "Turno Max", max_plus: "Turno Max+", clinica: "Clínica" };

export const MENSAJE_CUENTA_YA_ELIMINADA = "Esta cuenta ya fue eliminada.";
const IR_AL_INGRESO = () => {
  window.location.href = "/authentication";
};

const normalizar = (s) => String(s || "").trim().toLowerCase();

const detalleDe = (e) => {
  const detalle = e?.response?.data?.detail;
  return typeof detalle === "string" ? detalle : "";
};

/** ¿El texto escrito habilita el botón final? Correo (sin mayúsculas ni espacios) o ELIMINAR. */
export const confirmacionValida = (typed, email) => {
  const t = String(typed || "").trim();
  return t === "ELIMINAR" || (Boolean(normalizar(email)) && normalizar(t) === normalizar(email));
};

/** Suscripción activa sin cancelación programada: la que obliga a cancelar primero (R53). */
export const suscripcionBloquea = (suscripcion) =>
  Boolean(suscripcion && suscripcion.status === "activa" && !suscripcion.cancel_at_period_end);

// Lo que se conserva, en el orden del mockup 2A. El tercero (revisor O4, #1683): el registro de
// accesos a fichas (`FichaAccessLog`) guarda el correo de quien miró y se conserva 730 días (design.md
// D8); el paso 1 tiene que decirlo, no prometer que el correo desaparece de todas partes.
// "hasta 2 años" y no "por 2 años": parte de ese registro se purga a los 365 días y el resto a los 730.
export const SE_CONSERVA = [
  "Boletas y facturas de lo que pagaste",
  "El registro de que aceptaste los Términos",
  "El registro de las fichas de pacientes que abriste, con tu correo, hasta 2 años",
];

// Lo de WhatsApp que borra la app es el permiso y su copia de los datos (hilos, eventos, salidas). La
// conversación misma vive en el Mini (registro de atención y CRM, design.md D13) y eliminar la cuenta
// NO la borra: el paso 1 no puede prometer "tus mensajes de WhatsApp" (revisor O11, #1683).
export const SE_BORRA = [
  "Tus pacientes y pestañas del Turno",
  "Tus documentos y notas generadas",
  "Tu perfil, firma y archivos subidos",
  "Tu permiso y tus datos de WhatsApp en la app",
];
export const NOTA_WHATSAPP =
  "La conversación de WhatsApp queda en nuestro registro de atención; pídenos borrarla por WhatsApp.";

// Revisor O3 (#1683, design.md D5): con un plan vigente (aunque ya esté cancelado al fin del
// período) eliminar corta el cobro y lo que quedaba del período se pierde sin reembolso.
export const AVISO_SIN_REEMBOLSO =
  "Si tienes un plan, se cancela su cobro y pierdes lo que quedaba del período pagado, sin reembolso.";

// "No se borró nada" + a quién escribir (revisor v4 B8): el texto del servidor dice "escríbenos por
// WhatsApp" y al lado va el link (el mismo número que la pausa por inactividad).
export const CODIGOS_CON_SOPORTE = ["flow_no_cancelo", "flow_estado_desconocido", "flow_error_api", "no_se_pudo_eliminar"];

// Solo cuando el servidor lo afirma (`no_se_pudo_eliminar`): la transacción se revirtió sin tocar Flow.
const MENSAJE_FALLO_ELIMINAR =
  "No pudimos eliminar la cuenta. No se borró nada. Intenta de nuevo o escríbenos a privacidad@smardx.com.";
export const MENSAJE_COBRO_CANCELADO =
  "Cancelamos el cobro de tu suscripción, pero no pudimos eliminar la cuenta. Tu cuenta sigue activa sin cobro. Vuelve a intentarlo.";
// Sin respuesta del servidor (red, proxy, timeout) no sabemos si la cuenta se eliminó.
export const MENSAJE_SIN_RESPUESTA =
  "No pudimos confirmar si tu cuenta se eliminó. Recarga la página: si sigues dentro, vuelve a intentarlo o escríbenos a privacidad@smardx.com.";

// height y minHeight: el theme global de MuiButton pisa solo minHeight y dejaba el botón en 42 px (R60).
const botonBase = { height: 44, minHeight: 44, borderRadius: 999, px: 2.25, fontSize: 13.5, fontWeight: 600, textTransform: "none" };
const sxGhost = {
  ...botonBase,
  color: "var(--text-secondary)",
  border: "1px solid rgba(var(--fg-rgb),0.16)",
  "&:hover": { backgroundColor: "rgba(var(--fg-rgb),0.06)", border: "1px solid rgba(var(--fg-rgb),0.16)" },
};
const sxPrimario = {
  ...botonBase,
  backgroundColor: "var(--color-primary)",
  color: "var(--text-on-primary)",
  "&:hover": { backgroundColor: "var(--color-primary-soft)" },
};
const sxPeligro = {
  ...botonBase,
  backgroundColor: "var(--color-danger)",
  color: "var(--text-on-danger)",
  "&:hover": { backgroundColor: "var(--color-danger)", opacity: 0.9 },
  "&.Mui-disabled": { backgroundColor: "var(--color-danger)", color: "var(--text-on-danger)", opacity: 0.38 },
};

const Grupo = ({ tono, titulo, items, testid }) => (
  <Box
    data-testid={testid}
    sx={{
      borderRadius: "10px",
      p: "10px 12px",
      mb: 1.1,
      fontSize: 12.5,
      border: "1px solid rgba(var(--fg-rgb),0.08)",
      backgroundColor: tono === "del" ? "rgba(var(--color-danger-rgb),0.06)" : "rgba(var(--fg-rgb),0.035)",
    }}
  >
    <Box
      sx={{
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 0.3,
        mb: 0.5,
        color: tono === "del" ? "var(--color-danger)" : "var(--text-secondary)",
      }}
    >
      {titulo}
    </Box>
    <Box component="ul" sx={{ m: 0, pl: 2.1, color: "var(--text-tertiary)", "& li": { my: 0.25 } }}>
      {items.map((t) => (
        <li key={t}>{t}</li>
      ))}
    </Box>
  </Box>
);

const Pasos = ({ activo }) => (
  <Stack direction="row" spacing={0.75} sx={{ mb: 1.5 }} aria-hidden="true">
    {[1, 2].map((n) => (
      <Box
        key={n}
        sx={{
          flex: 1,
          height: 4,
          borderRadius: 99,
          backgroundColor: n <= activo ? "var(--color-primary)" : "rgba(var(--fg-rgb),0.14)",
        }}
      />
    ))}
  </Stack>
);

const EliminarCuentaDialog = ({ open, onClose, currentUser, refreshUser }) => {
  const email = currentUser?.email || "";
  const suscripcion = currentUser?.suscripcion || null;
  const [variante, setVariante] = useState("paso1"); // "suscripcion" | "paso1" | "paso2" | "eliminada"
  const [typed, setTyped] = useState("");
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState("");
  const [conSoporte, setConSoporte] = useState(false);
  const inputRef = useRef(null);

  // Cada apertura parte de cero y elige la variante según /me (R53).
  useEffect(() => {
    if (open) {
      setVariante(suscripcionBloquea(suscripcion) ? "suscripcion" : "paso1");
      setTyped("");
      setError("");
      setConSoporte(false);
      setTrabajando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const cancelarSuscripcion = async () => {
    setTrabajando(true);
    setError("");
    try {
      await postCancelarSuscripcion("eliminar_cuenta");
      try {
        if (refreshUser) await refreshUser();
      } catch (_) {
        // El servidor ya canceló: si /me no refresca, el diálogo igual sigue.
      }
      setVariante("paso1");
    } catch (e) {
      const detalle = e?.response?.data?.detail;
      setError(
        typeof detalle === "string" && detalle
          ? detalle
          : "No pudimos cancelar tu suscripción. No se cambió nada: intenta de nuevo en un rato."
      );
    } finally {
      setTrabajando(false);
    }
  };

  const eliminar = async () => {
    if (!confirmacionValida(typed, email) || trabajando) return;
    setTrabajando(true);
    setError("");
    setConSoporte(false);
    try {
      await postEliminarCuenta(typed.trim());
    } catch (e) {
      const code = codigoDeError(e);
      if (code === "cuenta_ya_eliminada") {
        // Otra pestaña ya la eliminó: la sesión de esta pestaña no sirve más.
        try {
          limpiarSesionLocal();
        } catch (_) {
          /* igual se le lleva al ingreso */
        }
        setError(detalleDe(e) || MENSAJE_CUENTA_YA_ELIMINADA);
        setVariante("eliminada");
      } else if (code === "suscripcion_activa") {
        try {
          if (refreshUser) await refreshUser();
        } catch (_) {
          /* el diálogo cambia de variante igual */
        }
        setVariante("suscripcion");
      } else if (CODIGOS_CON_SOPORTE.includes(code)) {
        // No se borró nada (revisor B1/B4, O15, v4 B8): el texto del servidor dice por qué, si conviene
        // reintentar, y que nos escriba por WhatsApp (el link va debajo).
        setError(detalleDe(e) || MENSAJE_FALLO_ELIMINAR);
        setConSoporte(true);
      } else if (code === "cobro_cancelado_cuenta_no_eliminada") {
        // Flow ya cortó el cobro pero la cuenta no se eliminó (revisor B4): /me cambió.
        setError(detalleDe(e) || MENSAJE_COBRO_CANCELADO);
        try {
          if (refreshUser) await refreshUser();
        } catch (_) {
          /* el mensaje ya dice lo que pasó */
        }
      } else if (code === "cuenta_privilegiada") {
        setError("Una cuenta con permisos de administración no se elimina desde aquí.");
      } else if (code && detalleDe(e)) {
        // Otro rechazo con código del servidor (p. ej. confirmación inválida): nada se hizo.
        setError(detalleDe(e));
      } else {
        setError(MENSAJE_SIN_RESPUESTA);
      }
      setTrabajando(false);
      return;
    }
    // Ya no hay cuenta: misma limpieza que cerrar sesión y pantalla pública final (R55).
    try {
      limpiarSesionLocal();
    } finally {
      window.location.href = "/cuenta-eliminada";
    }
  };

  const nombrePlan = PLAN_NOMBRES[suscripcion?.plan] || "tu plan";

  return (
    <Dialog
      open={open}
      onClose={trabajando ? undefined : variante === "eliminada" ? IR_AL_INGRESO : onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        "data-testid": "eliminar-cuenta-dialogo",
        sx: {
          backgroundColor: "var(--bg-elevated)",
          backgroundImage: "none",
          border: "1px solid rgba(var(--fg-rgb),0.16)",
          borderRadius: "16px",
          p: 2.5,
          m: { xs: 2, sm: 4 },
          width: "100%",
          maxWidth: 420,
          boxShadow: "0 18px 50px rgba(var(--shadow-rgb),0.4)",
        },
      }}
    >
      {variante === "suscripcion" && (
        <Box data-testid="eliminar-cuenta-suscripcion">
          <Typography component="h3" sx={{ fontSize: 17, fontWeight: 700, mb: 0.5 }}>
            Primero hay que cancelar tu suscripción
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "var(--text-muted)", mb: 1.5 }}>
            No podemos eliminar una cuenta con un plan vigente.
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 1.25,
              p: "11px 13px",
              mb: 1.5,
              borderRadius: "10px",
              fontSize: 12.5,
              color: "var(--text-secondary)",
              backgroundColor: "rgba(var(--color-warning-rgb),0.1)",
              border: "1px solid rgba(var(--color-warning-rgb),0.35)",
            }}
          >
            <WarningAmberIcon sx={{ fontSize: 18, flex: "none", mt: "1px", color: "var(--color-warning)" }} />
            <Box>
              Tienes el <b>Plan {nombrePlan}</b> activo. Al cancelarlo no se te cobra más. Seguirás con acceso
              hasta el fin del período que ya pagaste.
            </Box>
          </Box>
          <Typography sx={{ fontSize: 12.5, color: "var(--text-muted)" }}>
            Después de cancelar, vuelve aquí y podrás continuar con la eliminación.
          </Typography>
          {error && (
            <Typography role="alert" data-testid="eliminar-cuenta-error" sx={{ mt: 1.5, fontSize: 13, fontWeight: 600, color: "var(--color-danger)" }}>
              {error}
            </Typography>
          )}
          <Stack direction="row" spacing={1.25} justifyContent="flex-end" flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
            <Button onClick={onClose} disabled={trabajando} sx={sxGhost}>
              Ahora no
            </Button>
            <Button
              data-testid="eliminar-cuenta-cancelar-suscripcion"
              onClick={cancelarSuscripcion}
              disabled={trabajando}
              sx={sxPrimario}
            >
              Cancelar mi suscripción
            </Button>
          </Stack>
        </Box>
      )}

      {variante === "eliminada" && (
        <Box data-testid="eliminar-cuenta-eliminada">
          <Typography component="h3" sx={{ fontSize: 17, fontWeight: 700, mb: 1 }}>
            Cuenta eliminada
          </Typography>
          <Typography role="alert" data-testid="eliminar-cuenta-error" sx={{ fontSize: 13.5, color: "var(--text-secondary)" }}>
            {error}
          </Typography>
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button data-testid="eliminar-cuenta-ir-ingreso" onClick={IR_AL_INGRESO} sx={sxPrimario}>
              Ir al ingreso
            </Button>
          </Stack>
        </Box>
      )}

      {variante === "paso1" && (
        <Box data-testid="eliminar-cuenta-paso1">
          <Pasos activo={1} />
          <Typography component="h3" sx={{ fontSize: 17, fontWeight: 700, mb: 0.5 }}>
            Qué pasa si eliminas tu cuenta
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "var(--text-muted)", mb: 1.5 }}>
            Revisa esto antes de seguir.
          </Typography>
          <Grupo
            tono="del"
            titulo="SE BORRA"
            testid="eliminar-cuenta-se-borra"
            items={SE_BORRA}
          />
          <Grupo
            tono="keep"
            titulo="SE CONSERVA POR LEY"
            testid="eliminar-cuenta-se-conserva"
            items={SE_CONSERVA}
          />
          <Typography data-testid="eliminar-cuenta-nota-whatsapp" sx={{ fontSize: 12.5, color: "var(--text-muted)", mb: 1 }}>
            {NOTA_WHATSAPP}
          </Typography>
          {suscripcion?.status === "activa" && (
            <Typography data-testid="eliminar-cuenta-sin-reembolso" sx={{ fontSize: 12.5, color: "var(--text-secondary)", mb: 1 }}>
              {AVISO_SIN_REEMBOLSO}
            </Typography>
          )}
          <Typography sx={{ fontSize: 12.5, color: "var(--text-muted)" }}>
            Las copias de respaldo se van renovando solas, así que tus datos pueden tardar un tiempo en
            desaparecer de ellas.
          </Typography>
          <Stack direction="row" spacing={1.25} justifyContent="flex-end" flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
            <Button data-testid="eliminar-cuenta-cancelar" onClick={onClose} sx={sxGhost}>
              Cancelar
            </Button>
            <Button data-testid="eliminar-cuenta-continuar" onClick={() => setVariante("paso2")} sx={sxPrimario}>
              Continuar
            </Button>
          </Stack>
        </Box>
      )}

      {variante === "paso2" && (
        <Box data-testid="eliminar-cuenta-paso2">
          <Pasos activo={2} />
          <Typography component="h3" sx={{ fontSize: 17, fontWeight: 700, mb: 0.5 }}>
            Confirma que eres tú
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "var(--text-muted)", mb: 1.5 }}>
            Para evitar errores, escribe tu correo o la palabra ELIMINAR.
          </Typography>
          <Typography
            component="label"
            htmlFor="eliminar-cuenta-confirmacion"
            sx={{ display: "block", fontSize: 12.5, color: "var(--text-tertiary)", mb: 0.75 }}
          >
            Escribe <b>{email}</b> o <b>ELIMINAR</b>
          </Typography>
          <TextField
            id="eliminar-cuenta-confirmacion"
            fullWidth
            autoFocus
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            disabled={trabajando}
            inputRef={inputRef}
            autoComplete="off"
            inputProps={{ "data-testid": "eliminar-cuenta-confirmacion", spellCheck: false, "aria-label": "Confirmación" }}
            onKeyDown={(e) => {
              if (e.key === "Enter") eliminar();
            }}
          />
          {error && (
            <Typography role="alert" data-testid="eliminar-cuenta-error" sx={{ mt: 1.5, fontSize: 13, fontWeight: 600, color: "var(--color-danger)" }}>
              {error}
            </Typography>
          )}
          {error && conSoporte && (
            // Mismo link que la pausa por inactividad (`PausaInactividadGate` LinkSoporte), mismo token:
            // `--accent-onboarding` cambia de modo (el `--color-primary` en oscuro no da contraste de texto).
            <Link
              data-testid="eliminar-cuenta-whatsapp"
              href={WHATSAPP_SOPORTE}
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
              sx={{ display: "inline-flex", alignItems: "center", gap: "6px", mt: 1, fontSize: 13, fontWeight: 600, color: "var(--accent-onboarding)" }}
            >
              <WhatsAppIcon sx={{ fontSize: 15 }} />
              Escríbenos por WhatsApp
            </Link>
          )}
          <Stack direction="row" spacing={1.25} justifyContent="flex-end" flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
            <Button data-testid="eliminar-cuenta-volver" onClick={() => setVariante("paso1")} disabled={trabajando} sx={sxGhost}>
              Volver
            </Button>
            <Button
              data-testid="eliminar-cuenta-confirmar"
              onClick={eliminar}
              disabled={!confirmacionValida(typed, email) || trabajando}
              sx={sxPeligro}
            >
              {trabajando ? "Eliminando…" : "Eliminar cuenta"}
            </Button>
          </Stack>
        </Box>
      )}
    </Dialog>
  );
};

/** La zona de peligro al final de la Cuenta + su diálogo (R51). */
export const EliminarCuentaZona = ({ currentUser, refreshUser }) => {
  const [abierto, setAbierto] = useState(false);
  // Una cuenta con poder no se autoelimina (el servidor igual responde 403): no se le ofrece un
  // botón que siempre falla. `/me` no trae `can_manage_users`; ese caso lo ataja el 403 del diálogo.
  const privilegiada = Boolean(currentUser?.is_superuser || currentUser?.is_staff);
  return (
    <>
      <Box
        data-testid="eliminar-cuenta-zona"
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          p: 2.25,
          borderRadius: 3,
          border: "1px solid rgba(var(--color-danger-rgb),0.35)",
          backgroundColor: "rgba(var(--color-danger-rgb),0.05)",
        }}
      >
        <Box>
          <Typography component="h3" sx={{ fontSize: 15, fontWeight: 700, color: "var(--color-danger)" }}>
            Eliminar mi cuenta
          </Typography>
          <Typography sx={{ fontSize: 13, color: "var(--text-muted)", maxWidth: 520, mt: 0.4 }}>
            {privilegiada
              ? "Las cuentas con permisos de administración no se eliminan desde aquí."
              : "Borra tu cuenta y lo que guardaste en ella. No se puede deshacer."}
          </Typography>
        </Box>
        <Button
          data-testid="eliminar-cuenta-abrir"
          onClick={() => setAbierto(true)}
          disabled={privilegiada}
          startIcon={<DeleteOutlineIcon />}
          sx={{
            ...botonBase,
            color: "var(--color-danger)",
            border: "1px solid rgba(var(--color-danger-rgb),0.5)",
            "&:hover": { backgroundColor: "rgba(var(--color-danger-rgb),0.08)" },
            "&.Mui-disabled": { color: "var(--color-danger)", opacity: 0.38 },
          }}
        >
          Eliminar mi cuenta
        </Button>
      </Box>
      <EliminarCuentaDialog
        open={abierto}
        onClose={() => setAbierto(false)}
        currentUser={currentUser}
        refreshUser={refreshUser}
      />
    </>
  );
};

export default EliminarCuentaDialog;
